// src/context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState, useRef } from "react";
import axiosInstance from "../api/axiosInstance";

const getJwtExpiry = (token) => {
 if (!token) return null;
 try {
 const payload = JSON.parse(atob(token.split(".")[1]));
 return payload.exp * 1000;
 } catch {
 return null;
 }
};

const normalizeUserSession = (session) => {
 if (!session) return null;
 return {
 id: session.userId ?? null,
 userId: session.userId ?? null,
 email: session.email ?? "",
 name: session.name ?? "",
 mobile: session.mobile ?? session.phone ?? "",
 picture: session.picture ?? null,
 googleId: session.googleId ?? null,
 referralCode: session.referralCode ?? null,
 referralLink: session.referralLink ?? null,
 timeZone: session.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
 status: session.status ?? "ACTIVE",
 userType: session.userType ?? "EMAIL"
 };
};


/**
 * ===============================
 * Create context
 * ===============================
 */
const AuthContext = createContext(null);


/**
 * ===============================
 * Provider
 * ===============================
 */
export const AuthProvider = ({ children }) => {

 const [user, setUser] = useState(null);
 const [isSuspended, setIsSuspended] = useState(false);
 const [authLoading, setAuthLoading] = useState(true);

 const refreshTimeout = useRef(null);


 /**
 * ===============================
 * Load user from localStorage
 * ===============================
 */
 const loadUser = () => {
 const storedUser = localStorage.getItem("user");
 if (!storedUser) { setUser(null); setAuthLoading(false); return; }
 try {
 setUser(JSON.parse(storedUser));
 scheduleTokenRefresh();
 } catch {
 setUser(null);
 localStorage.removeItem("user");
 localStorage.removeItem("token");
 localStorage.removeItem("refreshToken");
 } finally {
 setAuthLoading(false);
 }
 };


 useEffect(() => {

 loadUser();

 window.addEventListener("auth-change", loadUser);
 return () => window.removeEventListener("auth-change", loadUser);

 }, []);


 /**
 * ===============================
 * LOGIN (supports new UserSessionResponseDTO)
 * ===============================
 */
 const login = (sessionResponse, token) => {
 if (!sessionResponse) return;

 if (sessionResponse.status === "SUSPENDED") {
 localStorage.removeItem("token");
 localStorage.removeItem("refreshToken");
 localStorage.removeItem("user");
 setUser(null);
 return;
 }

 const normalizedUser = normalizeUserSession(sessionResponse);
 if (!normalizedUser) return;

 if (token) {
 localStorage.setItem("token", token);
 } else if (sessionResponse.token) {
 localStorage.setItem("token", sessionResponse.token);
 }

 if (sessionResponse.refreshToken) {
 localStorage.setItem("refreshToken", sessionResponse.refreshToken);
 }

 localStorage.setItem("user", JSON.stringify(normalizedUser));
 setUser(normalizedUser);
 scheduleTokenRefresh();
 window.dispatchEvent(new Event("auth-change"));
 };


 /**
 * ===============================
 * LOGOUT
 * ===============================
 */
 const logout = () => {
 clearTimeout(refreshTimeout.current);
 localStorage.removeItem("token");
 localStorage.removeItem("refreshToken");
 localStorage.removeItem("user");
 setUser(null);
 window.dispatchEvent(new Event("auth-change"));
 };


 /**
 * ===============================
 * Schedule silent refresh
 * ===============================
 */
 const scheduleTokenRefresh = () => {
 const token = localStorage.getItem("token");
 if (!token) return;

 const expiry = getJwtExpiry(token);
 if (!expiry) return;

 const refreshIn = expiry - Date.now() - 10 * 60 * 1000;

 if (refreshIn <= 0) { refreshToken(); return; }

 clearTimeout(refreshTimeout.current);
 refreshTimeout.current = setTimeout(refreshToken, refreshIn);
 };


 /**
 * ===============================
 * Refresh token
 * ===============================
 */
 const refreshToken = async () => {
 const storedRefreshToken = localStorage.getItem("refreshToken");
 if (!storedRefreshToken) { logout(); return; }

 try {
 const res = await axiosInstance.post("/auth/refresh", { refreshToken: storedRefreshToken });
 localStorage.setItem("token", res.data.token);
 if (res.data.refreshToken) localStorage.setItem("refreshToken", res.data.refreshToken);
 scheduleTokenRefresh();
 } catch {
 logout();
 }
 };


 /**
 * ===============================
 * Provider
 * ===============================
 */
 return (
 <AuthContext.Provider
 value={{
 user,
 authLoading,
 isSuspended,
 setIsSuspended,
 login,
 logout
 }}
 >
 {children}
 </AuthContext.Provider>
 );
};


/**
 * ===============================
 * Hook
 * ===============================
 */
export const useAuth = () =>
 useContext(AuthContext);